// src/components/TopNav.js

import {
  Box,
  Flex,
  Heading,
  Text,
  Tooltip,
  IconButton,
  Image,
  Collapse,
  Input,
  InputGroup,
  useBreakpointValue,
  Link,
} from "@chakra-ui/react";
import { FaTelegramPlane } from "react-icons/fa";
import { MoonIcon, SunIcon, SearchIcon } from "@chakra-ui/icons";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";

export default function TopNav({
  colorMode,
  toggleColorMode,
  prizePool,
  countdownToNoon,
  showSearchBar,
  toggleSearchBar,
  searchTerm,
  handleSearch,
  isMobile,
}) {
  const isNarrow = useBreakpointValue({ base: true, xl: true, "2xl": false });

  const [isBackendActive, setIsBackendActive] = useState(false);

  const isActive = isBackendActive;

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch("/api/health", { cache: "no-store" });
        const json = await res.json().catch(() => ({}));
        setIsBackendActive(res.ok && json.ok === true);
      } catch {
        setIsBackendActive(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 10000); // check every 10s
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Flex
        justify="space-between"
        position="relative"
        zIndex={10}
        align="center"
        mb={12}
        wrap="nowrap"
        display={{ base: "none", md: "flex" }}
      >
        {/* Prize Pool */}
        <Box minW="240px">
          <Tooltip
            label="This is the current total prize pool amount."
            hasArrow
            placement="top"
            bg="white"
            color="gray.700"
            fontSize="sm"
            px={4}
            py={2}
            borderRadius="md"
            boxShadow="lg"
            textAlign="center"
            fontWeight="medium"
          >
            <Text fontSize="xl" fontWeight="semibold">
              💰 Prize Pool:{" "}
              <Box as="span" fontWeight="bold">
                {(prizePool + 11500).toLocaleString()} USDT
              </Box>
            </Text>
          </Tooltip>
        </Box>

        {/* Center Logo + Status Dot */}
        <Box
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
          display="flex"
          justifyContent="center"
        >
          <Flex position="relative" zIndex={10} align="center" gap={2}>
            <Link as={RouterLink} to="/">
              <Image
                src="/lotoro-logo.png"
                alt="Lotoro Logo"
                boxSize="50px"
                borderRadius="md"
              />
            </Link>
            <Link as={RouterLink} to="/">
              <Heading fontSize="5xl">Lotoro</Heading>
            </Link>
            <Tooltip
              label={isActive ? "Status: Active" : "Status: Not Active"}
              hasArrow
              placement="top"
              bg="white"
              color="gray.700"
              fontSize="xs"
              px={3}
              py={1}
              borderRadius="md"
              boxShadow="lg"
              fontWeight="medium"
            >
              <Box
                ml={1}
                mt={1}
                w="8px"
                h="8px"
                borderRadius="full"
                bg={isActive ? "green.400" : "red.400"}
                boxShadow="0 0 6px rgba(0,0,0,0.5)"
              />
            </Tooltip>
          </Flex>
        </Box>

        {/* Right Controls */}
        <Flex
          position="relative"
          zIndex={10}
          minW="240px"
          justify="flex-end"
          align="center"
          gap={1}
        >
          {!isNarrow && (
            <Tooltip
              label="Time remaining until the next prize payout and pool reset."
              hasArrow
              placement="top"
              bg="white"
              color="gray.700"
              fontSize="sm"
              px={4}
              py={2}
              borderRadius="md"
              boxShadow="lg"
              textAlign="center"
              fontWeight="medium"
            >
              <Text fontSize="xl" fontWeight="semibold">
                🕓 Next Payout:{" "}
                <Box as="span" fontWeight="bold">
                  {countdownToNoon}
                </Box>
              </Text>
            </Tooltip>
          )}

          <IconButton
            as={Link}
            href="https://t.me/lotoroco"
            isExternal
            size="md"
            fontSize="lg"
            variant="ghost"
            color="current"
            icon={<FaTelegramPlane />}
            aria-label="Telegram"
          />

          <IconButton
            size="md"
            fontSize="lg"
            variant="ghost"
            color="current"
            onClick={toggleColorMode}
            icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
            aria-label="Toggle theme"
          />

          <IconButton
            size="md"
            fontSize="lg"
            variant="ghost"
            color="current"
            onClick={toggleSearchBar}
            icon={<SearchIcon />}
            aria-label="Search"
            display={{ base: "none", md: "inline-flex" }}
          />

          <Collapse in={showSearchBar && !isMobile} animateOpacity>
            <Box width="200px">
              <InputGroup>
                <Input
                  type="text"
                  placeholder="Search your number"
                  value={searchTerm}
                  onChange={handleSearch}
                  borderRadius="full"
                  textAlign="center"
                />
              </InputGroup>
            </Box>
          </Collapse>

          <Box transform="scale(0.9)" transformOrigin="top right">
            <ConnectButton />
          </Box>
        </Flex>
      </Flex>

      {/* Bottom Left Payout for Narrow Screens */}
      {isNarrow && (
        <Box
          position="fixed"
          bottom="1rem"
          left="1rem"
          bg="transparent"
          zIndex={50}
        >
          <Tooltip
            label="Time remaining until the next Prize payout and Pool reset."
            hasArrow
            placement="top"
            bg="white"
            color="gray.700"
            fontSize="sm"
            px={4}
            py={2}
            borderRadius="md"
            boxShadow="lg"
            textAlign="center"
            fontWeight="medium"
          >
            <Text fontSize="xl" fontWeight="semibold">
              🕓 Next Payout:{" "}
              <Box as="span" fontWeight="bold">
                {countdownToNoon}
              </Box>
            </Text>
          </Tooltip>
        </Box>
      )}
    </>
  );
}
